import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { UserDTO, UserLiteDTO } from 'src/app/common/state/user/user.dto';
import { UserService } from 'src/app/common/state/user/user.service';
import {
  dateFromNgbDate,
  ngbDateStructFromDate,
  ngbDateStructFromUnixTimestamp,
} from 'src/app/common/utilities/date-helpers';
import { TodoType } from '../../enums/todo-type.enum';
import { TodoService } from '../../services/todo/todo.service';
import { CkEditorComponent } from '../ck-editor/ck-editor.component';
import { DatepickerComponent } from '../datepicker/datepicker.component';
import { ModalComponent } from '../modals/modal/modal.component';
import {
  ActionItemAPIPackage,
  ActionItemDTO,
  ActionPlanFiltersAPIPackage,
  UpdateActionItemAPIPackage,
} from '../todos/card-todos/todo.dto';
import { UserSelectComponent } from '../user-select/user-select.component';

@Component({
  selector: 'app-coaching-log-todos',
  standalone: false,
  templateUrl: './coaching-log-todos.component.html',
  styleUrl: './coaching-log-todos.component.scss',
})
export class CoachingLogTodosComponent implements OnInit, OnDestroy {
  subs: Subscription[] = [];

  actionItems: ActionItemDTO[] = [];

  actionItemForm: FormGroup;

  actionType: 'create' | 'edit';

  loading = true;

  @Input() coachlogId: number;

  @Input() clickSubject: Subject<boolean>;

  @ViewChild('actionPlanModal')
  actionPlanModal: ModalComponent;

  @ViewChild('editor')
  editor: CkEditorComponent;

  @ViewChild('userSelect')
  userSelect: UserSelectComponent;

  @ViewChild('datepicker')
  datepicker: DatepickerComponent;

  selectedDate = new BehaviorSubject<number>(1620000000);

  selectedUserIds: number[] = [];

  isEmpty = true;

  logAttendees: UserDTO[] = [];

  currentActionItemUserId: number | undefined;

  editId = '';

  constructor(
    public todoService: TodoService,
    private formBuilder: FormBuilder,
    private userService: UserService
  ) {}

  ngOnInit() {
    this.getActionItems();
    this.resetForm();

    this.subs.push(
      this.todoService.actionTodos.subscribe((todos) => {
        if (todos) {
          this.actionItems = todos;
          this.loading = false;
        }
      })
    );

    this.subs.push(
      this.clickSubject.subscribe((resp) => {
        if (resp) {
          this.resetForm();
          this.actionPlanModal.open();
        }
      })
    );
  }

  resetForm() {
    this.currentActionItemUserId = undefined;
    if (this.actionPlanModal) {
      this.actionPlanModal.config.titleText = 'To Do';
    }
    this.actionItemForm = this.formBuilder.group({
      title: ['', Validators.required],
    });
    if (this.editor) {
      this.editor.reset();
      this.editId = '';
    }
    if (this.userSelect) {
      this.userSelect.reset();
      this.userSelect.isMulti = true;
      this.userSelect.disabled = false;
    }
    if (this.datepicker) {
      this.datepicker.selectedDate = ngbDateStructFromDate(new Date());
    }
    this.actionType = 'create';
  }

  checkIfEditorEmpty() {
    if (this.editor) {
      if (this.editor.editorContent === '') {
        this.isEmpty = true;
        return true;
      }
      this.isEmpty = false;
      return false;
    }
    this.isEmpty = true;
    return true;
  }

  getActionItems() {
    const filters: ActionPlanFiltersAPIPackage = {
      ref_table: 'egrowe_coachlog',
      ref_table_id: this.coachlogId,
    };
    this.todoService.getActionItems(filters);
  }

  selectUserChange(event: UserLiteDTO[] | null) {
    if (event) {
      this.selectedUserIds = event.map((user) => user.id);
    }
  }

  selectUserEdit(event: UserLiteDTO | null) {
    if (event) {
      this.selectedUserIds[0] = event.id;
    }
  }

  saveActionItem() {
    let payloadUser: number | undefined = this.currentActionItemUserId;
    if (this.editId) {
      if (this.selectedUserIds[0]) {
        payloadUser = this.selectedUserIds[0];
      }

      const payload: UpdateActionItemAPIPackage = {
        coachlog_id: this.coachlogId,
        content: this.actionItemForm.controls['title'].value,
        description: this.editor.editorContent,
        due_date:
          dateFromNgbDate(this.datepicker.selectedDate).getTime() / 1000,
        user_id: payloadUser,
      };
      this.todoService
        .updateActionItem(parseInt(this.editId), payload)
        .subscribe(() => {
          this.selectedUserIds = [];
          this.currentActionItemUserId = undefined;
        });
    } else {
      const payload: ActionItemAPIPackage = {
        coachlog_id: this.coachlogId,
        content: this.actionItemForm.controls['title'].value,
        description: this.editor.editorContent,
        due_date:
          dateFromNgbDate(this.datepicker.selectedDate).getTime() / 1000,
        type: TodoType.EGROWE,
        userTodoMeta: {
          ref_table: 'egrowe_coachlog',
          ref_table_id: this.coachlogId,
        },
        users: this.selectedUserIds,
      };
      this.todoService.createActionItem(payload);
      this.selectedUserIds = [];
    }
  }

  editActionItem(id: number) {
    this.actionType = 'edit';

    const actionItem = this.actionItems.find((item) => item.id === id);
    if (actionItem) {
      this.currentActionItemUserId = actionItem.userId;
      this.actionItemForm = this.formBuilder.group({
        title: [actionItem.content, Validators.required],
      });
      this.editor.setEditorContent(actionItem.description);
      this.userService.fetchUserById(actionItem.userId).subscribe((userDTO) => {
        if (userDTO) {
          this.userSelect.internalSelectedUserList = [];
          this.userSelect.isMulti = false;
          this.userSelect.internalSelectedUser = userDTO;
        }
      });
      this.datepicker.selectedDate = ngbDateStructFromUnixTimestamp(
        actionItem.dueDate
      );
      this.editId = id.toString();
      this.actionPlanModal.config.titleText = 'Edit To Do';
      this.actionPlanModal.open();
    }
  }

  deleteActionItem(id: number) {
    this.todoService.deleteActionItem(id, this.coachlogId).subscribe();
  }

  ngOnDestroy() {
    let sub = this.subs.pop();
    while (sub) {
      sub.unsubscribe();
      sub = this.subs.pop();
    }
  }
}
